import React, { useEffect, useRef, useState } from "react";
import { Check, X, Gauge, Sparkles } from "lucide-react";
import { FONT, T, focusRing } from "./tokens";

// ===========================================================================
// HealthChip — SEO / health score in the frame header. A compact score chip
// that expands into a checklist (title, meta, schema, mobile, image alts).
// Green = passing; each miss offers an "Ask Vella to fix" action.
//
// Props: checks: [{id,label,ok,hint}], onFix(check)
// ===========================================================================

function scoreColor(pct) {
  if (pct >= 80) return T.green;
  if (pct >= 50) return T.amber;
  return T.red;
}

export default function HealthChip({ checks = [], onFix }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const passed = checks.filter((c) => c.ok).length;
  const pct = checks.length ? Math.round((passed / checks.length) * 100) : 100;
  const color = scoreColor(pct);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!rootRef.current || !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={`Site health ${pct}% — ${passed} of ${checks.length} checks passing`}
        onClick={() => setOpen((o) => !o)}
        onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 30,
          padding: "0 12px",
          borderRadius: T.radiusFull,
          border: `1px solid ${T.line}`,
          background: "rgba(255,255,255,0.7)",
          color: T.ink2,
          fontFamily: FONT,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <Gauge size={13} strokeWidth={2} style={{ color }} aria-hidden="true" />
        <span>Health</span>
        <span style={{ fontWeight: 700, color }}>{pct}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Site health checklist"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            zIndex: 40,
            width: 288,
            padding: 12,
            borderRadius: T.radiusMd,
            background: T.surface,
            border: `1px solid ${T.line}`,
            boxShadow: T.shadowLg,
            fontFamily: FONT,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Site health</span>
            <span style={{ fontSize: 12, fontWeight: 700, color }}>
              {passed}/{checks.length}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {checks.map((c) => (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "7px 8px",
                  borderRadius: T.radiusSm,
                  background: c.ok ? "transparent" : "rgba(201,92,82,0.06)",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: T.radiusFull,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: c.ok ? "rgba(63,138,99,0.14)" : "rgba(201,92,82,0.14)",
                    color: c.ok ? T.green : T.red,
                  }}
                >
                  {c.ok ? <Check size={12} strokeWidth={2.6} /> : <X size={12} strokeWidth={2.6} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.label}</div>
                  {!c.ok && c.hint && (
                    <div style={{ fontSize: 11.5, color: T.ink3, lineHeight: 1.35 }}>{c.hint}</div>
                  )}
                </div>
                {!c.ok && (
                  <button
                    type="button"
                    onClick={() => {
                      onFix && onFix(c);
                      setOpen(false);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                      height: 24,
                      padding: "0 9px",
                      borderRadius: T.radiusFull,
                      border: `1px solid ${T.blue}`,
                      background: "rgba(39,92,115,0.06)",
                      color: T.blue,
                      fontFamily: FONT,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <Sparkles size={11} strokeWidth={2} aria-hidden="true" />
                    Fix
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
